import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableItem({ id, value, displayValue, onStartEdit, onEdit, onEndEdit, onRemove, isEditing }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="d-flex align-items-center gap-2 mb-2 p-2 border rounded bg-white"
    >
      <span
        className="text-muted"
        style={{ cursor: "grab" }}
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        ⋮⋮
      </span>
      {isEditing ? (
        <input
          type="text"
          className="form-control form-control-sm flex-grow-1"
          value={displayValue}
          onChange={(e) => onEdit(id, e.target.value)}
          onBlur={onEndEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.target.blur();
          }}
          maxLength={300}
          autoFocus
        />
      ) : (
        <span
          className="flex-grow-1"
          onClick={() => onStartEdit(id)}
          style={{ cursor: "pointer", minHeight: "1.5em" }}
        >
          {value || "(empty)"}
        </span>
      )}
      <button
        type="button"
        className="btn btn-sm btn-outline-danger"
        onClick={() => onRemove(id)}
        aria-label="Remove"
      >
        ×
      </button>
    </div>
  );
}

export default function AmenitiesList({ amenities = [], onChange }) {
  const [inputValue, setInputValue] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const items = Array.isArray(amenities) ? amenities : [];
  const ids = items.map((_, i) => i);

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    onChange([...items, trimmed]);
    setInputValue("");
  };

  const handleRemove = (index) => {
    onChange(items.filter((_, i) => i !== index));
    if (editingId === index) setEditingId(null);
  };

  const handleEditStart = (index, currentValue) => {
    setEditingId(index);
    setEditingValue(currentValue);
  };

  const handleEditChange = (index, value) => {
    setEditingValue(value);
    const next = [...items];
    next[index] = value;
    onChange(next);
  };

  const handleEditEnd = () => {
    if (editingId == null) return;
    const next = [...items];
    next[editingId] = (editingValue || "").trim();
    onChange(next.filter(Boolean));
    setEditingId(null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setEditingId(null);
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(active.id);
      const newIndex = ids.indexOf(over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onChange(arrayMove(items, oldIndex, newIndex));
      }
    }
  };

  return (
    <div className="mb-3">
      <label className="form-label">Amenities</label>
      <div className="d-flex gap-2 mb-2">
        <input
          type="text"
          className="form-control"
          placeholder="e.g. WiFi, TV, Mini bar"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAdd())}
          maxLength={300}
        />
        <button type="button" className="btn btn-outline-secondary" onClick={handleAdd}>
          Add
        </button>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {items.map((value, index) => (
            <SortableItem
              key={index}
              id={index}
              value={value}
              displayValue={editingId === index ? editingValue : value}
              onStartEdit={(id) => handleEditStart(id, items[id])}
              onEdit={handleEditChange}
              onEndEdit={handleEditEnd}
              onRemove={handleRemove}
              isEditing={editingId === index}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}
